/**
 * TransactionColumnRegistry
 *
 * Defines standard transaction table columns with fixed semantic identity.
 * Columns have stable keys, default labels, format hints, and pure accessor functions.
 * Client config overrides labels and visibility — the key always means the same thing.
 *
 * Configuration via clientData.transaction_columns_config:
 *   {
 *     strikePrice: { visible: true, label: "Cost Basis" },
 *     taxBasis: { visible: true },
 *     sharePrice: { label: "Gift FMV" },
 *     referencePrice: { visible: true },  // show in table (normally export-only)
 *   }
 *
 * Falls back to legacy flags/language when transaction_columns_config is absent.
 *
 * Custom (per-client, non-built-in) columns via clientData.transaction_custom_columns
 * -- shape and philosophy deliberately mirror Cap Table Custom Columns (CTCC,
 * see docs/cap-table-custom-columns.md and transaction-custom-column-value.js
 * for the full explanation of what's shared with CTCC and what's deliberately
 * not, namely storage location). Only ONE custom column is supported per
 * client today (2026-08-05), always under the fixed key "customColumn" --
 * only its LABEL is admin-chosen/relabelable (defaults to "Custom"; BW's is
 * currently labeled "Gift FMV"):
 *   transaction_custom_columns: [
 *     { key: "customColumn", label: "Gift FMV", source: "custom", format: "currency",
 *       includeInExport: true, showInTableUI: true },
 *   ]
 * These merge into this registry's column list alongside STANDARD_COLUMNS below,
 * so a custom column participates in getVisibleColumns()/getExportColumns()/
 * getLabel() etc. exactly like a built-in one. See
 * transaction-custom-column-value.js's buildTransactionCustomColumnDefinitions().
 * Relabeling isn't just a display change -- for EXTERNAL_DATA (CSV-backed)
 * clients, the physical CSV header itself is renamed to match (see
 * TransactionService.js's renameExternalCustomColumnHeader()), since the
 * column is recognized on read/write by matching its current label, not a
 * fixed system name.
 *
 * Usage:
 *   const registry = new TransactionColumnRegistry(clientData);
 *   const columns = registry.getVisibleColumns({ isConsolidatedView: true });
 *   columns.forEach(col => {
 *     const label = registry.getLabel(col.key);
 *     const value = col.accessor(transaction);
 *   });
 */

import { normalizeDateString } from './date-utils.js';
import { buildTransactionCustomColumnDefinitions, listTransactionCustomColumns } from './transaction-custom-column-value.js';

function parseNum(val) {
	if (val == null || val === '') return null;
	if (typeof val === 'number') return isNaN(val) ? null : val;
	const cleaned = String(val).replace(/[^-.\d]/g, '');
	const num = Number(cleaned);
	return isNaN(num) ? null : num;
}

/**
 * Build a transaction_columns_config from legacy clientData flags and language.
 * Used when clientData.transaction_columns_config is not set.
 */
function buildConfigFromLegacy(clientData) {
	const config = {};
	const flags = clientData.flags || {};
	const language = clientData.language || {};

	// Label overrides from language. Populate BOTH Ledgers' original keys
	// (sharePrice/strikePrice/taxBasis) and Events & Transactions' independent
	// keys (sharePriceET/strikePriceET/taxBasisET) identically here (2026-08-04)
	// -- see the "*ET" STANDARD_COLUMNS entries below for why these are
	// separate, explicitly-configurable columns rather than one shared setting.
	// Before either surface had its own settings UI, both were always driven by
	// these same legacy flags/language values, so this preserves that identical
	// default behavior for every client that's never touched either surface's
	// new column settings -- divergence only happens once an admin explicitly
	// configures ONE surface via its own modal (see ledgers.js's
	// saveTxnColumnSettings and shared_assets/index.js's renderETColumnSettings,
	// each of which only ever writes its own surface's keys).
	if (language.label_cost_price) {
		config.sharePrice = { ...config.sharePrice, label: language.label_cost_price };
		config.sharePriceET = { ...config.sharePriceET, label: language.label_cost_price };
	}
	if (language.label_tax_basis) {
		config.taxBasis = { ...config.taxBasis, label: language.label_tax_basis };
		config.taxBasisET = { ...config.taxBasisET, label: language.label_tax_basis };
	}
	if (language.label_strike_price) {
		config.strikePrice = { ...config.strikePrice, label: language.label_strike_price };
		config.strikePriceET = { ...config.strikePriceET, label: language.label_strike_price };
	}

	// Label overrides from language flags
	if (language.label_investments) {
		config.shareClass = { label: language.label_investments };
	} else if (clientData.use_real_estate_language) {
		config.shareClass = { label: 'Property' };
		config.quantity = { label: 'Ownership' };
	} else if (clientData.use_fund_language) {
		config.shareClass = { label: 'Fund' };
		config.quantity = { label: 'Contribution' };
	}

	// Visibility from legacy flags -- again populating both the Ledgers and
	// Events & Transactions keys identically.
	if (flags.use_tax_basis) {
		// Legacy behavior: use_tax_basis showed strikePrice column with the tax basis label
		const strikeLabelFromTaxBasis = language.label_tax_basis || 'Tax Basis';
		config.strikePrice = { ...config.strikePrice, visible: true, label: config.strikePrice?.label || strikeLabelFromTaxBasis };
		config.strikePriceET = { ...config.strikePriceET, visible: true, label: config.strikePriceET?.label || strikeLabelFromTaxBasis };
	}
	if (flags.show_tax_basis_column) {
		config.taxBasis = { ...config.taxBasis, visible: true };
		config.taxBasisET = { ...config.taxBasisET, visible: true };
	}

	// Re-added 2026-08-04: removed 2026-08-03 as dead-for-Ledgers code (its
	// accessor always returned null there, and this registry wasn't consumed
	// by anything else). Now that Events & Transactions has its own settings
	// UI backed by this same registry (see shared_assets/index.js's
	// setLabels()), 'balance' is real again -- E&T computes its own value via
	// the balanceHistory/directionForUser walk in renderUserTransactionsModal,
	// this just controls whether that column shows. Legacy fallback preserves
	// existing behavior for any client that's never touched the new settings
	// UI (e.g. Marvin, via flags.show_ET_balances). Unlike sharePrice/
	// strikePrice/taxBasis, 'balance' was already Events & Transactions-only
	// from the start, so it doesn't need a separate Ledgers/ET key pair.
	if (flags.show_ET_balances) config.balance = { ...config.balance, visible: true };

	return config;
}

export const STANDARD_COLUMNS = [
	{
		key: 'shareClass',
		defaultLabel: 'Class',
		format: 'text',
		accessor: (txn) => txn.shareClass,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'holder',
		defaultLabel: 'Account',
		format: 'text',
		accessor: (txn) => txn.holderName || txn.holder || '',
		visibility: 'conditional',
		visibilityRule: (config) => !!config._isConsolidatedView,
		includeInExport: false,
	},
	{
		key: 'date',
		defaultLabel: 'Date',
		format: 'date',
		accessor: (txn) => txn.date,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'securityType',
		defaultLabel: 'Security Type',
		format: 'text',
		accessor: (txn) => txn.securityType || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'certNumber',
		defaultLabel: 'Cert #',
		format: 'text',
		accessor: (txn) => txn.certNumber || '',
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.certNumber?.visible,
		includeInExport: false,
		neverExport: true,
	},
	{
		key: 'description',
		defaultLabel: 'Description',
		format: 'text',
		accessor: (txn) => txn.notes || '',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'nrid',
		defaultLabel: 'nrid',
		format: 'text',
		accessor: (txn) => txn.nrid || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'sender',
		defaultLabel: 'Sender',
		format: 'text',
		accessor: (txn) => txn.senderName || 'Issuer',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'senderEmail',
		defaultLabel: 'Sender Email',
		format: 'text',
		accessor: (txn) => txn.sender || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'receiver',
		defaultLabel: 'Receiver',
		format: 'text',
		accessor: (txn) => txn.receiverName || 'Issuer',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'receiverEmail',
		defaultLabel: 'Receiver Email',
		format: 'text',
		accessor: (txn) => txn.receiver || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'quantity',
		defaultLabel: 'Quantity',
		format: 'number',
		accessor: (txn) => txn.quantity,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'sharePrice',
		defaultLabel: 'Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		// Changed 2026-08-03: was visibility: 'always' (locked on, no opt-out).
		// Chris wants Share Price hideable per-client, defaulting to visible for
		// every existing client that hasn't said otherwise -- so this reads
		// "hidden only if explicitly set to false", not "visible only if
		// explicitly set to true" like every other conditional column here.
		// See ledgers.js's ALL_STANDARD_COLUMNS (now locked: false, defaultVisible:
		// true) and its saveTxnColumnSettings handler, which had to learn to
		// write visible:false explicitly when this one gets turned off --
		// otherwise omitting the key from the saved snapshot would fall right
		// back to visible on the next load.
		visibility: 'conditional',
		visibilityRule: (config) => config._colConfig.sharePrice?.visible !== false,
		includeInExport: true,
	},
	{
		key: 'exactPrice',
		defaultLabel: 'Exact Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.exactPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'referencePrice',
		defaultLabel: 'Reference Price',
		format: 'currency',
		accessor: (txn) => txn.referencePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.referencePrice?.visible,
		includeInExport: true,
	},
	{
		key: 'qtyTimesPrice',
		defaultLabel: 'Qty x Price',
		format: 'currency',
		accessor: (txn) => {
			const qty = parseNum(txn.quantity);
			const price = parseNum(txn.sharePrice);
			return qty != null && price != null ? qty * price : null;
		},
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.qtyTimesPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'qtyTimesRefPrice',
		defaultLabel: 'Qty x Ref. Price',
		format: 'currency',
		accessor: (txn) => {
			const qty = parseNum(txn.quantity);
			const refPrice = parseNum(txn.referencePrice);
			return qty != null && refPrice != null ? qty * refPrice : null;
		},
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.qtyTimesRefPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'balance',
		defaultLabel: 'Balance',
		format: 'number',
		// Re-added 2026-08-04 (see buildConfigFromLegacy's show_ET_balances
		// comment above for the full history). This accessor is intentionally
		// a stub -- unlike every other column here, a running balance can't be
		// computed from a single transaction in isolation; it requires walking
		// the user's full transaction history in date order. Events &
		// Transactions computes the real value itself (balanceHistory /
		// directionForUser in shared_assets/index.js's renderUserTransactionsModal)
		// and only asks this registry whether the column should show and what
		// to call it. Ledgers has no such per-account walk (it lists every
		// holder's transactions together) and explicitly forces this column
		// invisible at render time regardless of what this resolves to -- see
		// ledgers.js's runtimeTransactionColumnsConfig.balance override.
		accessor: () => null,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.balance?.visible,
		includeInExport: false,
	},
	{
		key: 'strikePrice',
		defaultLabel: 'Strike Price',
		format: 'currency',
		accessor: (txn) => txn.strikePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.strikePrice?.visible,
		includeInExport: true,
	},
	{
		key: 'expDate',
		defaultLabel: 'Expiration Date',
		format: 'date',
		// Fixed 2026-07-31, two passes. First pass: this threw RangeError:
		// Invalid time value (uncaught, crashing the whole Ledgers table
		// render) whenever txn.expDate was truthy but not a clean unix-seconds
		// value -- Number(txn.expDate) went to NaN, new Date(NaN) is a valid
		// "Invalid Date" object, and .toISOString() throws on it. Since expDate
		// is export-only (visibilityRule below is always false),
		// formatExportCell() calls this accessor unconditionally for every
		// transaction row, so a single bad value took down the entire table.
		// Second pass: the crash-safe version fell back to '' for anything that
		// wasn't a clean unix-seconds number -- but real data has expDate
		// stored as human-formatted strings too (e.g. "Aug 16 2024", confirmed
		// directly from an export), so that "safe" version was silently
		// blanking out real expiration dates instead of showing them. Routed
		// through normalizeDateString() (date-utils.js), which already handles
		// both shapes -- unix-seconds numbers and the same dateFormats list
		// (number-formats.js) used everywhere else in this app for loosely-
		// formatted date strings, "MMM D YYYY" included -- and never throws,
		// returning '' only for genuinely unparseable values.
		accessor: (txn) => normalizeDateString(txn.expDate, true),
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'taxBasis',
		defaultLabel: 'Tax Basis',
		format: 'currency',
		accessor: (txn) => txn.taxBasis,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.taxBasis?.visible,
		includeInExport: true,
	},
	// Note: the client's one custom column (key: 'customColumn', e.g. BW's
	// "Gift FMV") is NOT a hardcoded entry here -- it's a
	// transaction_custom_columns entry (see this file's top docstring and
	// transaction-custom-column-value.js), merged into this registry's column
	// list at construction time. It's inline-editable in Ledgers and Events &
	// Transactions only (see transaction-table-renderer.js's 'customColumn'
	// case, ledgers.js's renderLedgerCustomColumnListeners, and
	// shared_assets/index.js's renderEventsTransCustomColumnListeners) -- the
	// +New/Edit transaction modals don't capture it, by design; admins fill
	// it in via inline edit after the transaction is created.

	// ── Events & Transactions-only variants (2026-08-04) ──────────────────
	// Ledgers (an admin, all-holders-at-once view) and Events & Transactions
	// (a shareholder-facing, per-account view) both let an admin configure
	// Share Price / Strike Price / Tax Basis -- but per Chris, the two
	// audiences are different enough that the two surfaces shouldn't be
	// forced to always show/label these identically. These *ET columns are
	// otherwise identical twins of sharePrice/strikePrice/taxBasis above --
	// same accessors, same legacy-flag defaults (see buildConfigFromLegacy,
	// which populates both key pairs together) -- just resolved from their
	// own, independent transaction_columns_config keys. Ledgers' settings UI
	// (ledgers.js) only ever reads/writes the plain keys above; Events &
	// Transactions' settings UI (shared_assets/index.js's ET_STANDARD_COLUMNS)
	// only ever reads/writes these. Until an admin explicitly saves either
	// surface's settings, both resolve identically via the shared legacy
	// fallback, so nothing changes for any client that hasn't touched either.
	{
		key: 'sharePriceET',
		defaultLabel: 'Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		visibility: 'conditional',
		visibilityRule: (config) => config._colConfig.sharePriceET?.visible !== false,
		includeInExport: false,
	},
	{
		key: 'strikePriceET',
		defaultLabel: 'Strike Price',
		format: 'currency',
		accessor: (txn) => txn.strikePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.strikePriceET?.visible,
		includeInExport: false,
	},
	{
		key: 'taxBasisET',
		defaultLabel: 'Tax Basis',
		format: 'currency',
		accessor: (txn) => txn.taxBasis,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.taxBasisET?.visible,
		includeInExport: false,
	},
];

export class TransactionColumnRegistry {
	constructor(clientData = {}) {
		// Resolve column config as a PER-COLUMN merge (2026-08-04), not an
		// all-or-nothing choice between "explicit config" and "legacy flags".
		// Previously this was `clientData.transaction_columns_config ||
		// buildConfigFromLegacy(clientData)` -- the moment a client had ANY
		// explicit transaction_columns_config saved (from either Ledgers or
		// Events & Transactions), the ENTIRE legacy fallback stopped applying,
		// even for columns the saved config never mentioned. That was the
		// documented root cause of the original Cost Basis relabeling
		// confusion, and it would have silently broken Events & Transactions'
		// Balance column too: a client with flags.show_ET_balances still true
		// but an unrelated transaction_columns_config already saved (e.g. from
		// Ledgers hiding Share Price) would lose Balance entirely, since
		// nothing in that saved config ever mentioned 'balance'. Per Chris:
		// legacy flags/settings should keep winning per-column until an admin
		// actually configures that specific column via the new settings UI.
		// Per-client column list: built-in STANDARD_COLUMNS plus this client's
		// transaction_custom_columns (2026-08-05, see this file's top docstring
		// and transaction-custom-column-value.js). Custom columns are appended,
		// never prepended/inserted -- canonical column order for built-ins is
		// unaffected, matching every consumer's assumption (e.g. the CSV export
		// scraper's reliance on STANDARD_COLUMNS' fixed order) that a client's
		// custom columns land in whatever position they're declared, at the end.
		this._columns = STANDARD_COLUMNS.concat(buildTransactionCustomColumnDefinitions(clientData));

		const legacyConfig = buildConfigFromLegacy(clientData);
		const explicitConfig = clientData.transaction_columns_config || {};
		this._colConfig = {};
		this._columns.forEach((col) => {
			const legacyVal = legacyConfig[col.key];
			const explicitVal = explicitConfig[col.key];
			if (legacyVal || explicitVal) {
				this._colConfig[col.key] = { ...legacyVal, ...explicitVal };
			}
		});
		this._columnMap = new Map();
		this._labels = new Map();

		// Build lookup map
		this._columns.forEach((col) => {
			this._columnMap.set(col.key, col);
		});

		// Resolve labels: config label > default label
		this._columns.forEach((col) => {
			const colConf = this._colConfig[col.key];
			const label = colConf?.label || col.defaultLabel;
			this._labels.set(col.key, label);
		});
	}

	getLabel(key) {
		return this._labels.get(key);
	}

	/**
	 * Override a single column's resolved label dynamically. Used by callers
	 * that need to inject a per-class label (#5298 / U5) — the registry is
	 * built client-wide, so per-class context arrives after construction.
	 * @param {string} key - Column key (e.g., 'quantity')
	 * @param {string|null|undefined} label - Override label; falsy values restore the original
	 */
	setLabel(key, label) {
		if (!this._columnMap.has(key)) return;
		if (label) {
			this._labels.set(key, label);
		} else {
			// Restore the default label from config / column default.
			const col = this._columnMap.get(key);
			const colConf = this._colConfig[key];
			this._labels.set(key, colConf?.label || col.defaultLabel);
		}
	}

	getColumn(key) {
		return this._columnMap.get(key);
	}

	getAllColumns() {
		return this._columns.slice();
	}

	/**
	 * Returns columns that should be visible in the table UI.
	 */
	getVisibleColumns(context = {}) {
		const merged = this._mergeContext(context);
		return this._columns.filter((col) => {
			if (col.visibility === 'always') return true;
			if (col.visibility === 'conditional') return col.visibilityRule(merged);
			return false;
		});
	}

	/**
	 * Returns columns for export: all visible columns + any column with includeInExport.
	 * Columns can opt out via config: { includeInExport: false }
	 */
	getExportColumns(context = {}) {
		const merged = this._mergeContext(context);
		return this._columns.filter((col) => {
			// Hard exclusion — visible in table but never in CSV export
			if (col.neverExport) return false;
			const colConf = this._colConfig[col.key];
			// Client config opt-out
			if (colConf?.includeInExport === false) return false;
			// Include if visible
			if (col.visibility === 'always') return true;
			if (col.visibility === 'conditional' && col.visibilityRule(merged)) return true;
			// Include if column default says export even when not visible
			if (col.includeInExport) return true;
			return false;
		});
	}

	_mergeContext(context) {
		return {
			_colConfig: this._colConfig,
			_isConsolidatedView: !!context.isConsolidatedView,
		};
	}
}

// ─────────────────────────────────────────────────────────────────────────
// Shared column-settings modal UI + save logic (2026-08-05)
//
// Ledgers (admin/ledgers/ledgers.js's renderTxnColumnSettings/
// saveTxnColumnSettings) and Events & Transactions (shared_assets/index.js's
// renderETColumnSettings/saveETColumnSettings) each had their own ~250-line,
// near-byte-for-byte-identical copy of this exact panel-rendering and
// save-config-merge logic -- the only real differences were which column
// list and which DOM element ids they operated on, and what happens after a
// successful save (Ledgers re-renders its table; E&T rebuilds its modal).
// Centralized here rather than in a new file, since this is already the one
// module both surfaces import their column definitions from. Each caller
// still owns: its own columnDefs/getAllStandardColumns (see ledgers.js /
// index.js), its own two DOM containers, and its own POST + post-save
// callback -- everything else duplicated across both copies lives here now.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Builds and wires the "Available Columns" / "Visible Columns" drag-drop
 * panels, including per-column edit/apply/cancel-relabel controls. Renders
 * once immediately; the returned renderPanels() is exposed only in case a
 * caller ever needs to force a re-render from outside (neither does today).
 *
 * @param {Object} clientData
 * @param {Array} columnDefs - e.g. ledgers.js's/index.js's getAllStandardColumns(clientData)
 * @param {HTMLElement} availableEl - container for hidden/available column cards
 * @param {HTMLElement} selectedEl - container for visible column items
 * @returns {{ renderPanels: Function, getState: () => {visibleKeys: Set, labelEdits: Object} }}
 */
export function initColumnSettingsPanels({ clientData, columnDefs, availableEl, selectedEl }) {
	const registry = new TransactionColumnRegistry(clientData);
	const visibleKeys = new Set(registry.getVisibleColumns().map((c) => c.key));
	// Always-visible columns are always in the visible set
	columnDefs.forEach((col) => {
		if (col.locked) visibleKeys.add(col.key);
	});

	const labelEdits = {};
	columnDefs.forEach((col) => {
		// Fallback to col.defaultLabel covers the not-yet-created custom column
		// placeholder (registry.getLabel returns undefined for a column that
		// doesn't exist in the registry at all yet).
		labelEdits[col.key] = registry.getLabel(col.key) || col.defaultLabel;
	});

	// Drag state
	let draggedKey = null;
	let dragSource = null; // 'available' or 'selected'

	function renderPanels() {
		availableEl.innerHTML = '';
		selectedEl.innerHTML = '';

		// ── Left panel: available (hidden) columns ──
		columnDefs.forEach((col) => {
			if (col.locked || visibleKeys.has(col.key)) return;

			const card = document.createElement('div');
			card.className = 'ctcc-field-card';
			card.dataset.colKey = col.key;
			card.draggable = true;
			card.innerHTML = `
				<i class="material-icons" style="font-size:1rem;color:#666;">add</i>
				<span class="field-name">${col.defaultLabel}</span>`;

			card.addEventListener('click', function () {
				visibleKeys.add(col.key);
				renderPanels();
			});
			card.addEventListener('dragstart', function (e) {
				draggedKey = col.key;
				dragSource = 'available';
				e.dataTransfer.setData('text/plain', col.key);
				e.dataTransfer.effectAllowed = 'copy';
				card.classList.add('dragging');
			});
			card.addEventListener('dragend', function () {
				card.classList.remove('dragging');
				draggedKey = null;
				dragSource = null;
			});

			availableEl.appendChild(card);
		});

		if (!availableEl.children.length) {
			availableEl.innerHTML = '<p style="color:#999;font-size:0.8rem;padding:0.5rem;">All columns are visible.</p>';
		}

		// ── Right panel: visible (selected) columns ──
		columnDefs.forEach((col) => {
			if (!col.locked && !visibleKeys.has(col.key)) return;

			const currentLabel = labelEdits[col.key];
			const isRenamed = currentLabel !== col.defaultLabel;

			const item = document.createElement('div');
			item.dataset.colKey = col.key;
			item.className = col.locked ? 'ctcc-selected-item ctcc-locked-item' : 'ctcc-selected-item';
			item.style.alignItems = 'center';

			// View state: label + base name subtitle
			const viewEl = document.createElement('div');
			viewEl.className = 'txn-col-view';
			viewEl.style.cssText = 'flex:1;min-width:0;';
			viewEl.innerHTML = `
				<span class="column-name">${String(currentLabel || '').replace(/</g, '&lt;')}</span>
				<span class="txn-col-base">${col.defaultLabel}${isRenamed ? ' — renamed' : ''}</span>`;

			// Edit state: input + apply/cancel + reset link
			const editEl = document.createElement('div');
			editEl.className = 'txn-col-edit';
			editEl.style.cssText = 'display:none;flex:1;min-width:0;';
			editEl.innerHTML = `
				<input type="text" value="${String(currentLabel || '').replace(/"/g, '&quot;')}" placeholder="${col.defaultLabel}" style="width:100%;border:none;border-bottom:1px solid #26a69a;font-size:0.875rem;padding:2px 4px;outline:none;background:transparent;">
				${isRenamed ? `<span class="reset-link" style="font-size:0.75rem;display:block;margin-top:2px;">Reset to "${col.defaultLabel}"</span>` : ''}`;

			const actionsEl = document.createElement('div');
			actionsEl.className = 'column-actions';
			actionsEl.innerHTML = `
				<i class="material-icons edit-btn" title="Edit label">edit</i>
				<i class="material-icons apply-btn" title="Apply" style="display:none;color:#1976d2;">check</i>
				<i class="material-icons cancel-btn" title="Cancel" style="display:none;color:#999;">close</i>
				${col.locked ? '' : '<i class="material-icons remove-btn" title="Remove">close</i>'}`;

			if (col.locked) {
				const lockIcon = document.createElement('i');
				lockIcon.className = 'material-icons drag-handle';
				lockIcon.textContent = 'lock';
				item.appendChild(lockIcon);
			}
			item.appendChild(viewEl);
			item.appendChild(editEl);
			item.appendChild(actionsEl);

			const editInput = editEl.querySelector('input');
			const resetLink = editEl.querySelector('.reset-link');
			const editBtn = actionsEl.querySelector('.edit-btn');
			const applyBtn = actionsEl.querySelector('.apply-btn');
			const cancelBtn = actionsEl.querySelector('.cancel-btn');
			const removeBtn = actionsEl.querySelector('.remove-btn');

			function enterEdit() {
				viewEl.style.display = 'none';
				editEl.style.display = '';
				editBtn.style.display = 'none';
				if (removeBtn) removeBtn.style.display = 'none';
				applyBtn.style.display = '';
				cancelBtn.style.display = '';
				editInput.focus();
				editInput.select();
			}
			function applyEdit() {
				labelEdits[col.key] = editInput.value.trim() || col.defaultLabel;
				exitEdit();
				renderPanels();
			}
			function exitEdit() {
				editInput.value = labelEdits[col.key]; // restore previous value on cancel
				viewEl.style.display = '';
				editEl.style.display = 'none';
				editBtn.style.display = '';
				if (removeBtn) removeBtn.style.display = '';
				applyBtn.style.display = 'none';
				cancelBtn.style.display = 'none';
			}

			editBtn.addEventListener('click', function (e) {
				e.stopPropagation();
				enterEdit();
			});
			applyBtn.addEventListener('click', function (e) {
				e.stopPropagation();
				applyEdit();
			});
			cancelBtn.addEventListener('click', function (e) {
				e.stopPropagation();
				exitEdit();
			});
			editInput.addEventListener('keydown', function (e) {
				if (e.key === 'Enter') applyEdit();
				if (e.key === 'Escape') exitEdit();
			});
			if (resetLink) {
				resetLink.addEventListener('click', function (e) {
					e.stopPropagation();
					editInput.value = col.defaultLabel;
				});
			}

			if (removeBtn) {
				removeBtn.addEventListener('click', function (e) {
					e.stopPropagation();
					visibleKeys.delete(col.key);
					renderPanels();
				});
			}

			// Accept drops from available panel onto selected items
			item.addEventListener('dragover', function (e) {
				e.preventDefault();
				if (draggedKey && dragSource === 'available') e.dataTransfer.dropEffect = 'copy';
			});
			item.addEventListener('drop', function (e) {
				e.preventDefault();
				e.stopPropagation();
				if (!draggedKey || dragSource !== 'available') return;
				visibleKeys.add(draggedKey);
				draggedKey = null;
				dragSource = null;
				renderPanels();
			});

			selectedEl.appendChild(item);
		});
	}

	// Drop zone for the selected panel (handles drops on empty space)
	selectedEl.addEventListener('dragover', function (e) {
		e.preventDefault();
		if (draggedKey) e.dataTransfer.dropEffect = dragSource === 'available' ? 'copy' : 'move';
	});
	selectedEl.addEventListener('drop', function (e) {
		e.preventDefault();
		if (!draggedKey) return;
		if (dragSource === 'available') {
			visibleKeys.add(draggedKey);
			draggedKey = null;
			dragSource = null;
			renderPanels();
		}
	});

	renderPanels();

	return {
		renderPanels,
		getState: () => ({ visibleKeys, labelEdits }),
	};
}

/**
 * Builds the merge-safe transaction_columns_config patch to persist from a
 * settings modal's current state. Starts from whatever's already stored,
 * clears only the keys `columnDefs` manages, and rebuilds those from
 * `state` -- anything outside that set (e.g. Ledgers-only certNumber/holder,
 * or E&T-only balance) passes through untouched. The backend does a full
 * SET/replace of this whole attribute on save (see NewServerless's
 * transaction-columns-config.py) -- there's no server-side merge, so this
 * client-side merge is the only thing standing between a save from one
 * surface and silently wiping a setting only the other surface's UI
 * controls.
 */
export function buildColumnSettingsConfig({ clientData, columnDefs, state }) {
	const config = { ...(clientData.transaction_columns_config || {}) };
	columnDefs.forEach((col) => delete config[col.key]);

	columnDefs.forEach((col) => {
		const isVisible = col.locked || state.visibleKeys.has(col.key);
		const label = state.labelEdits[col.key];
		const isRenamed = label && label !== col.defaultLabel;
		// defaultVisible columns (currently just sharePrice/sharePriceET) need
		// visible:false written explicitly when turned off -- this key gets
		// fully cleared and rebuilt from scratch above, so simply omitting it
		// here would fall back to "visible" again on next load.
		const needsExplicitOff = col.defaultVisible && !isVisible;

		if (!col.locked && (isVisible || isRenamed || needsExplicitOff)) {
			config[col.key] = {};
			if (isVisible) config[col.key].visible = true;
			else if (needsExplicitOff) config[col.key].visible = false;
			if (isRenamed) config[col.key].label = label;
		} else if (col.locked && isRenamed) {
			config[col.key] = { label: label };
		}
	});

	return config;
}

/**
 * Builds the transaction_custom_columns update (creation/relabel) from a
 * settings modal's current state -- transaction_custom_columns is a
 * SEPARATE clientData field from transaction_columns_config above, where
 * the custom column's actual existence (key/label/source/format) gets
 * defined, not just its visibility/label override. Returns nulls when
 * there's nothing to create or change (a client that's never touched this
 * control gets no transaction_custom_columns update at all).
 */
export function buildCustomColumnUpdate({ clientData, columnDefs, state }) {
	const customColDef = columnDefs.find((c) => c.key === 'customColumn');
	if (!customColDef) return { customColumnsUpdate: null, renameOldLabel: '', renameNewLabel: '' };

	const wantsIt = state.visibleKeys.has('customColumn');
	const newLabel = state.labelEdits.customColumn || 'Custom';
	const existing = listTransactionCustomColumns(clientData)[0] || null;

	let customColumnsUpdate = null;
	let renameOldLabel = '';
	let renameNewLabel = '';

	if (wantsIt || existing) {
		customColumnsUpdate = [
			{
				key: 'customColumn',
				label: newLabel,
				source: 'custom',
				format: existing?.format || 'currency',
				includeInExport: existing?.includeInExport !== false,
				showInTableUI: wantsIt,
			},
		];
		if (existing?.label && existing.label !== newLabel) {
			renameOldLabel = existing.label;
			renameNewLabel = newLabel;
		}
	}

	return { customColumnsUpdate, renameOldLabel, renameNewLabel };
}
